<template>
  <div class="email-notifications" data-testid="email-notifications">
    <NotificationsPageHeader />
    <NotificationNotEnabledMessage
      v-if="!notificationEnabled"
      :message="disabledMessage"
    />
    <template v-else>
      <FcTabSwitcher
        v-model="activeTab"
        variant="underline"
        :tabs="tabs"
        data-testid="email-notifications__tab-switcher"
        @input="onTabChange"
      />
      <div class="email-notifications__panel">
        <ActiveSchedulesTable
          v-if="activeTab === TABS.ACTIVE"
          :initial-loading="initialLoadPending"
          :loading="loading.list"
          :show-history="isEmailActivityVisible"
          @create="openStepper()"
          @edit="openStepper"
          @view-activity="openActivity"
        />
        <CycleTemplatesTable
          v-else-if="activeTab === TABS.TEMPLATES"
          :initial-loading="initialLoadPending"
          :loading="loading.list"
          :show-history="isEmailActivityVisible"
          @create-template="openTemplateForm"
          @edit="openStepper"
          @view-activity="openActivity"
        />
        <NotificationActivityTable
          v-else-if="activeTab === TABS.ACTIVITY"
          scope="setup"
          :level="emailActivityLogLevel"
          :notification-id="activityNotificationId"
          :loading="activityTabLoading"
          data-testid-prefix="email-notifications__activity"
          @clear-notification-scope="clearActivityNotificationScope"
        />
      </div>
    </template>
    <NotificationStepperDialog
      v-model="stepper.open"
      :notification="stepper.notification"
      :initial-feature="stepper.feature"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import FcTabSwitcher from '../../../components/FcTabSwitcher/FcTabSwitcher.vue';
import NotificationNotEnabledMessage from '../../../components/NotificationNotEnabledMessage/NotificationNotEnabledMessage.vue';
import { NotificationActivityTable } from '../../../components/Notifications';
import {
  EMAIL_NOTIFICATION,
  NOTIFICATION_SETUP_QUERY,
  NOTIFICATION_SETUP_TABS,
} from '../../../utils/constants';
import NotificationsPageHeader from './components/NotificationsPageHeader.vue';
import ActiveSchedulesTable from './components/ActiveSchedulesTable.vue';
import CycleTemplatesTable from './components/CycleTemplatesTable.vue';
import NotificationStepperDialog from './components/NotificationStepperDialog.vue';

export default {
  name: 'EmailNotifications',
  components: {
    FcTabSwitcher,
    NotificationNotEnabledMessage,
    NotificationActivityTable,
    NotificationsPageHeader,
    ActiveSchedulesTable,
    CycleTemplatesTable,
    NotificationStepperDialog,
  },
  data() {
    return {
      TABS: NOTIFICATION_SETUP_TABS,
      activeTab: NOTIFICATION_SETUP_TABS.ACTIVE,
      stepper: { open: false, notification: null, feature: '' },
      activityNotificationId: '',
      disabledMessage: EMAIL_NOTIFICATION.MESSAGES.NOTIFICATION_NOT_ENABLED,
      initialLoadPending: true,
    };
  },
  computed: {
    ...mapGetters('emailNotifications', {
      notificationEnabled: 'fetchNotificationEnabled',
      activeScheduleNotifications: 'activeScheduleNotifications',
      templateNotifications: 'templateNotifications',
      emailActivityLogLevel: 'emailActivityLogLevel',
      isEmailActivityVisible: 'isEmailActivityVisible',
      loading: 'loading',
    }),
    tabs() {
      const tabs = [
        { id: NOTIFICATION_SETUP_TABS.ACTIVE, label: 'Notifications', count: (this.activeScheduleNotifications || []).length },
        { id: NOTIFICATION_SETUP_TABS.TEMPLATES, label: 'Templates', count: (this.templateNotifications || []).length },
      ];
      if (this.isEmailActivityVisible) {
        tabs.push({ id: NOTIFICATION_SETUP_TABS.ACTIVITY, label: 'Run history' });
      }
      return tabs;
    },
    activityTabLoading() {
      return this.loading.activity;
    },
  },
  created() {
    this.loadAll();
    this.applySetupQuery();
  },
  beforeDestroy() {
    this.activityNotificationId = '';
    this.stepper = { open: false, notification: null, feature: '' };
  },
  methods: {
    ...mapActions('emailNotifications', {
      loadNotificationEnabled: 'fetchNotificationEnabled',
      fetchNotificationConfig: 'fetchNotificationConfig',
      fetchAvailableNotifications: 'fetchAvailableNotifications',
      fetchAllCreatedNotifications: 'fetchAllCreatedNotifications',
      fetchLocations: 'fetchLocations',
      fetchCategories: 'fetchCategories',
    }),
    async loadAll() {
      try {
        await Promise.all([
          this.loadNotificationEnabled(),
          this.fetchNotificationConfig(),
          this.fetchAvailableNotifications(),
          this.fetchAllCreatedNotifications(),
          this.fetchLocations(),
          this.fetchCategories(),
        ]);
      } finally {
        this.initialLoadPending = false;
      }
    },
    queryParamValue(value) {
      if (Array.isArray(value)) return value[0] || '';
      return typeof value === 'string' ? value : '';
    },
    applySetupQuery() {
      const tab = this.queryParamValue(this.$route.query[NOTIFICATION_SETUP_QUERY.TAB]);
      if (tab && Object.values(NOTIFICATION_SETUP_TABS).includes(tab)) {
        this.activeTab = tab;
      }
      const notificationId = this.queryParamValue(this.$route.query[NOTIFICATION_SETUP_QUERY.NOTIFICATION]);
      if (notificationId) {
        this.activityNotificationId = notificationId;
      }
    },
    /**
     * Sync ?tab= and ?notification= without vue-router navigation.
     * base.vue keys non-Cycle-Settings router-view on $route.fullPath, so
     * router.replace remounts this page and drops activityNotificationId.
     * Same pattern as InformationRequests.syncTabQueryParam.
     */
    syncSetupQuery() {
      const query = { ...this.$route.query, [NOTIFICATION_SETUP_QUERY.TAB]: this.activeTab };
      if (this.activityNotificationId) {
        query[NOTIFICATION_SETUP_QUERY.NOTIFICATION] = this.activityNotificationId;
      } else {
        delete query[NOTIFICATION_SETUP_QUERY.NOTIFICATION];
      }
      const { href } = this.$router.resolve({ path: this.$route.path, query });
      window.history.replaceState(window.history.state, '', href);
    },
    onTabChange(id) {
      this.activeTab = id;
      this.syncSetupQuery();
    },
    clearActivityNotificationScope() {
      this.activityNotificationId = '';
      this.syncSetupQuery();
    },
    openStepper(notification = null) {
      this.stepper = {
        open: true,
        notification,
        feature: notification?.feature || '',
      };
    },
    openTemplateForm(feature) {
      this.stepper = { open: true, notification: null, feature };
    },
    openActivity(row) {
      this.activityNotificationId = row?._id || '';
      this.onTabChange(NOTIFICATION_SETUP_TABS.ACTIVITY);
    },
    onSaved() {
      this.fetchAllCreatedNotifications();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './EmailNotifications.scss';
</style>
